import React, { useState, useEffect } from 'react';
import CandidateLayout from '../../components/CandidateLayout';
import { solicitationsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Clock, 
  CheckCircle2, 
  XCircle,
  Loader2,
  Building2,
  Calendar,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

export default function CandidateSolicitations() {
  const [solicitations, setSolicitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchSolicitations();
  }, []);

  const fetchSolicitations = async () => {
    try {
      const response = await solicitationsAPI.getReceived();
      setSolicitations(response.data);
    } catch (error) {
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await solicitationsAPI.approve(id);
      toast.success('Acesso aprovado! Um chat foi criado.');
      fetchSolicitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await solicitationsAPI.reject(id);
      toast.success('Solicitação rejeitada');
      fetchSolicitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'APPROVED':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Aprovado</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <CandidateLayout>
      <div className="space-y-6" data-testid="candidate-solicitations">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Solicitações de Acesso</h1>
          <p className="text-slate-600">Empresas que querem acessar seu perfil</p>
        </div>

        {/* Info Card */}
        <Card className="border-cyan-200 bg-cyan-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-cyan-900">Seu consentimento é obrigatório</h3>
                <p className="text-sm text-cyan-700">
                  Empresas só podem ver seus dados completos após sua aprovação explícita.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Solicitations List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : solicitations.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Nenhuma solicitação
              </h3>
              <p className="text-slate-600">
                Quando empresas quiserem acessar seu perfil, aparecerá aqui
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {solicitations.map((solicitation) => (
              <Card 
                key={solicitation.id} 
                className="border-slate-200"
                data-testid={`solicitation-item-${solicitation.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(solicitation.status)}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">
                        {solicitation.company_name || 'Empresa'}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4" />
                        Solicitado em {formatDate(solicitation.created_at)}
                      </div>
                    </div>
                    
                    {solicitation.status === 'PENDING' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleReject(solicitation.id)}
                          disabled={processing === solicitation.id}
                          data-testid={`reject-btn-${solicitation.id}`}
                        >
                          {processing === solicitation.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Rejeitar'
                          )}
                        </Button>
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(solicitation.id)}
                          disabled={processing === solicitation.id}
                          data-testid={`approve-btn-${solicitation.id}`}
                        >
                          {processing === solicitation.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Aprovar'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </CandidateLayout>
  );
}
